import React, { useMemo } from 'react';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_Row } from 'material-react-table';
import { format, isValid } from 'date-fns';
import { Box, Button, Typography, Stack, Chip, alpha } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PRIMARY } from 'themes/colors';

interface IHistoryTableProp {
  title?: string;
  columns: Array<any>;
  rows: Array<any>;
  renderDetailPanel?: (props: { row: MRT_Row<any> }) => React.ReactNode;
}

const csvConfig = mkConfig({
  fieldSeparator: ',',
  decimalSeparator: '.',
  useKeysAsHeaders: true,
});

const GREEN = PRIMARY.main;
const GREEN_DARK = PRIMARY.dark;
const GREEN_LIGHT = PRIMARY.light;

function DataGrid({ title, columns, rows, renderDetailPanel }: IHistoryTableProp) {
  const memoizedColumns = useMemo<MRT_ColumnDef<any>[]>(() => {
    const columnsToIgnore = ['_id', 'user_id', 'createdAt', 'updatedAt', 'password', 'id', 'token'];
    return columns
      .filter((col) => !columnsToIgnore.includes(col.field ?? col.accessorKey))
      .map((col: any) => ({
        accessorKey: col.field ?? col.accessorKey,
        header: col.title ?? col.header,
        size: col.size ?? 150,
        Cell: ({ renderedCellValue, row }) => {
          const value = row.original[col.field ?? col.accessorKey];
          if (typeof value === 'boolean') return value ? 'Yes' : 'No';
          if (value instanceof Date || (typeof value === 'string' && value.includes('T'))) {
            try {
              const date = value instanceof Date ? value : new Date(value);
              if (isValid(date)) return format(date, 'dd.MM.yyyy');
            } catch {
              // fall through
            }
          }
          return renderedCellValue;
        },
      }));
  }, [columns]);

  const memoizedRows = useMemo(() => rows, [rows]);

  const handleExportRows = (rows: MRT_Row<any>[]) => {
    const rowData = rows.map((row) => {
      const { _id, user_id, createdAt, updatedAt, password, id, token, ...rest } = row.original;
      return rest;
    });
    download(csvConfig)(generateCsv(csvConfig)(rowData));
  };

  const handleExportData = () => {
    const rowData = memoizedRows.map((row) => {
      const { _id, user_id, createdAt, updatedAt, password, id, token, ...rest } = row;
      return rest;
    });
    download(csvConfig)(generateCsv(csvConfig)(rowData));
  };

  const handleExportPdf = () => {
    const doc = new jsPDF();
    const tableHeaders = memoizedColumns.map((c) => c.header);
    const tableData = memoizedRows.map((row) =>
      memoizedColumns.map((col) => row[col.accessorKey as string])
    );
    autoTable(doc, { head: [tableHeaders], body: tableData });
    doc.save('Jaman_Hp_Gas.pdf');
  };

  const handleExportSelectedRowsPdf = (rows: MRT_Row<any>[]) => {
    const doc = new jsPDF();
    const tableHeaders = memoizedColumns.map((c) => c.header);
    const tableData = rows.map((row) =>
      memoizedColumns.map((col) => row.original[col.accessorKey as string])
    );
    autoTable(doc, { head: [tableHeaders], body: tableData });
    doc.save('Jaman_Hp_Gas.pdf');
  };

  const ExportBtn = ({
    label,
    onClick,
    disabled,
    icon,
  }: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    icon?: React.ReactNode;
  }) => (
    <Button
      size="small"
      variant="outlined"
      disabled={disabled}
      onClick={onClick}
      startIcon={icon ?? <FileDownloadIcon />}
      sx={{
        textTransform: 'none',
        borderRadius: 1.5,
        fontWeight: 600,
        fontSize: '0.72rem',
        py: 0.5,
        px: 1.5,
        borderColor: alpha(GREEN_LIGHT, 0.4),
        color: GREEN_DARK,
        '&:hover': { borderColor: GREEN_LIGHT, bgcolor: alpha(GREEN_LIGHT, 0.06) },
        '&.Mui-disabled': { opacity: 0.4 },
      }}
    >
      {label}
    </Button>
  );

  const table = useMaterialReactTable({
    columns: memoizedColumns,
    data: memoizedRows,
    enableRowSelection: true,
    enableSorting: true,
    enableColumnFilters: true,
    enableRowNumbers: true,
    columnFilterDisplayMode: 'popover',
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } },

    // ── Modern table styling ──
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '0 0 12px 12px',
        border: '1px solid',
        borderColor: 'grey.200',
        borderTop: 'none',
        overflow: 'hidden',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        background: `linear-gradient(90deg, ${GREEN_DARK} 0%, ${GREEN_LIGHT} 100%)`,
        boxShadow: 'none',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        borderBottom: 'none',
        py: 1.5,
        '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.7) !important' },
        '& .Mui-active .MuiTableSortLabel-icon': { color: '#fff !important' },
        '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.75)' },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        bgcolor: row.index % 2 === 0 ? '#fff' : '#f8f8ff',
        '&:hover': { bgcolor: `${alpha(GREEN_LIGHT, 0.06)} !important` },
        transition: 'background-color 0.15s',
      },
    }),
    muiTableBodyCellProps: {
      sx: {
        fontSize: '0.8rem',
        py: 1.1,
        borderColor: 'grey.100',
        color: 'text.primary',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        borderTop: '1px solid',
        borderColor: 'grey.200',
        bgcolor: '#fafafa',
      },
    },
    muiTopToolbarProps: {
      sx: {
        borderBottom: '1px solid',
        borderColor: 'grey.100',
        bgcolor: '#fafaff',
        minHeight: 48,
      },
    },
    muiPaginationProps: {
      shape: 'rounded',
      showRowsPerPage: true,
    },

    renderTopToolbarCustomActions: ({ table }) => (
      <Stack direction="row" spacing={0.75} alignItems="center" p={1} flexWrap="wrap" useFlexGap>
        <ExportBtn label="CSV" onClick={handleExportData} icon={<FileDownloadIcon />} />
        <ExportBtn label="PDF" onClick={handleExportPdf} icon={<PictureAsPdfIcon />} />
        <ExportBtn
          label="All Rows"
          onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
          disabled={table.getPrePaginationRowModel().rows.length === 0}
        />
        <ExportBtn
          label="Page"
          onClick={() => handleExportRows(table.getRowModel().rows)}
          disabled={table.getRowModel().rows.length === 0}
        />
        <ExportBtn
          label="Selected CSV"
          onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
        />
        <ExportBtn
          label="Selected PDF"
          onClick={() => handleExportSelectedRowsPdf(table.getSelectedRowModel().rows)}
          disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
          icon={<PictureAsPdfIcon />}
        />
      </Stack>
    ),

    ...(renderDetailPanel && { enableExpanding: true, renderDetailPanel }),
  });

  return (
    <Box sx={{ mt: 1.5, mb: 1.5 }}>
      {title && (
        <Box
          sx={{
            background: `linear-gradient(90deg, ${GREEN_DARK} 0%, ${GREEN_LIGHT} 100%)`,
            px: 2.5,
            py: 1.5,
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <TableChartIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff', letterSpacing: 0.3, fontSize: '0.85rem' }}>
              {title}
            </Typography>
          </Stack>
          <Chip
            label={`${rows.length} records`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              bgcolor: 'rgba(255,255,255,0.18)',
              color: '#fff',
            }}
          />
        </Box>
      )}
      <MaterialReactTable table={table} />
    </Box>
  );
}

export default DataGrid;
